import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output} from '@angular/core';
import {NgClass, NgIf} from '@angular/common';
import {AppState} from '../../models/AppState';
import {
  PaymentButtonButtonComponent,
  PaymentCardButtonButtonComponent,
  PaymentPayButtonButtonComponent, PaymentSwishButtonButtonComponent, PaymentVippsButtonButtonComponent
} from '../buttons/payment-button.component';
import {PaymentLoyaltyButtonComponent} from '../buttons/payment-loyalty-button.component';
import {ApplePayButtonComponent, WalletButtonState} from '../buttons/apple-pay-button.component';
import {GooglePayButtonComponent} from '../buttons/google-pay-button.component';
import {LoadingSpinnerComponent} from './loading-spinner-component';
import MoneyUtils from '../../utils/Utils';
import {AuthService} from '../../services/auth.service';
import {TranslateService} from '../../services/translate.service';
import {ChargeItem} from '../../models/Order';

@Component({
  selector: 'app-payment-selector',
  standalone: true,
  template: `
    <div class="px-4">
      <!-- Prepaid is shown in direct mode too: it carries the guest's discount price, which the wallet
           buttons don't, so it must never be hidden behind "Betala på annat sätt". -->
      <div *ngIf="prepaid">
        <app-payment-loyalty-button [title1]="prepaidName" [title2]="prepaidLeft" [subTitle1]="'Pris med rabatt'" [subTitle2]="prepaidPrice"
                                    icon1="fa-badge-percent" icon2="fa-coins" [disabled]="disabled" (onClick)="pay('prepaid')"></app-payment-loyalty-button>
      </div>
      <!-- Direct wallet buttons (Apple Pay / Google Pay). When at least one wallet is available we stay in
           "direct mode": only these buttons (and prepaid) show, with a small link below to reveal the other methods. -->
      <div *ngIf="isDirectMode"
           class="wallet-area relative"
           [class.wallet-pending]="!walletFirstResolveDone"
           [class.wallet-resolved]="walletFirstResolveDone">
        <!-- Spinner overlays the area (absolute) so it doesn't affect height. -->
        <div *ngIf="walletShowSpinner" class="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <app-loading-spinner size="sm-md"></app-loading-spinner>
        </div>
        <!-- Wrapper fades in once both buttons resolved. Rows use invisible (visibility:hidden) during pending so
             iframes inside can still load; once resolved, unavailable rows switch to display:none to collapse. -->
        <div class="transition-opacity duration-200"
             [class.opacity-0]="!walletFirstResolveDone"
             [class.opacity-100]="walletFirstResolveDone">
          <div class="mt-4"
               [class.invisible]="!walletFirstResolveDone"
               [class.hidden]="walletFirstResolveDone && applePayState !== 'ready'">
            <app-apple-pay-button
              [totalSek]="totalSek"
              [tipParam]="tipParam"
              [payableRefs]="payableRefs"
              [chargeItems]="chargeItems"
              [plKey]="plKey"
              [itemRefs]="itemRefs"
              [receiptKey]="receiptKey"
              [declineRoute]="declineRoute"
              (stateChange)="onApplePayStateChange($event)"
              (processingChange)="onWalletProcessingChange($event)"
              (paymentComplete)="onDirectPaymentComplete.emit($event)"
              (paymentFailed)="onDirectPaymentFailed.emit($event)"></app-apple-pay-button>
          </div>
          <div *ngIf="bamboraMerchantNumber"
               class="mt-4"
               [class.invisible]="!walletFirstResolveDone"
               [class.hidden]="walletFirstResolveDone && !showGooglePayButton">
            <app-google-pay-button
              [totalSek]="totalSek"
              [tipParam]="tipParam"
              [payableRefs]="payableRefs"
              [chargeItems]="chargeItems"
              [plKey]="plKey"
              [itemRefs]="itemRefs"
              [receiptKey]="receiptKey"
              [bamboraMerchantNumber]="bamboraMerchantNumber"
              [declineRoute]="declineRoute"
              (stateChange)="onGooglePayStateChange($event)"
              (processingChange)="onWalletProcessingChange($event)"
              (paymentComplete)="onDirectPaymentComplete.emit($event)"
              (paymentFailed)="onDirectPaymentFailed.emit($event)"></app-google-pay-button>
          </div>
        </div>
      </div>
      <div *ngIf="showOtherMethodsLink" class="mt-6 text-center">
        <span class="text-base text-theme-text cursor-pointer underline" (click)="showOtherMethods = true">{{translate.getString('Betala på annat sätt')}}</span>
      </div>
      <ng-container *ngIf="showOtherButtons">
        <div *ngIf="viva">
          <app-payment-button src="https://storage.googleapis.com/heynow/media/apaygpaytr.png" [disabled]="disabled" (onClick)="pay('viva_smart')"></app-payment-button>
        </div>
        <div *ngIf="cardPayment && googlePayMode !== 'direct' && googlePayMode !== 'disabled'" class="mt-4">
          <app-payment-pay-button [disabled]="disabled" (onClick)="pay('card_gp')"></app-payment-pay-button>
        </div>
        <div *ngIf="cardPayment" class="mt-4">
          <app-payment-card-button [disabled]="disabled" (onClick)="pay('card')"></app-payment-card-button>
        </div>
        <div *ngIf="swishPayment" class="mt-4">
          <app-payment-swish-button [disabled]="disabled" (onClick)="pay('swish')"></app-payment-swish-button>
        </div>
        <div *ngIf="vippsPayment" class="mt-4">
          <app-payment-vipps-button [disabled]="disabled" (onClick)="pay('vipps')"></app-payment-vipps-button>
        </div>
        <div *ngIf="invoicePayment" class="mt-4">
          <app-payment-button icon="fa-file-invoice" [text]="translate.getString('Betala med faktura')" [disabled]="disabled" (onClick)="pay('account')"></app-payment-button>
        </div>
        <div *ngIf="accountEntryPayment" class="mt-4">
          <app-payment-button icon="fa-file-invoice" [text]="translate.getString('Betala med samlingsfaktura')" [disabled]="disabled" (onClick)="pay('account')"></app-payment-button>
        </div>
      </ng-container>
      <!-- Wallet payment in progress: the OS sheet has closed but start() hasn't resolved yet.
           Cover the gap so the user sees that something is still happening before the redirect. -->
      <div *ngIf="paymentProcessing" class="fixed inset-0 bg-theme-surface-80 bg-opacity-75 flex items-center justify-center z-[100]">
        <app-loading-spinner size="xl"></app-loading-spinner>
      </div>
    </div>
  `,
  imports: [
    NgClass,
    NgIf,
    PaymentButtonButtonComponent,
    PaymentLoyaltyButtonComponent,
    PaymentCardButtonButtonComponent,
    PaymentPayButtonButtonComponent,
    PaymentSwishButtonButtonComponent,
    PaymentVippsButtonButtonComponent,
    ApplePayButtonComponent,
    GooglePayButtonComponent,
    LoadingSpinnerComponent
  ],
  styles: [`
    /* Reserve max wallet height during pending (2 buttons + their mt-4 margins) so the bottom
       button never jumps down when wallets resolve. After resolution, min-height collapses to 0
       via a smooth transition; the actual content height (1 or 0 buttons) is then revealed. */
    .wallet-area { transition: min-height 250ms ease-out; }
    .wallet-pending { min-height: 144px; }
    .wallet-resolved { min-height: 0; }
  `]
})
export class PaymentSelectorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() appState!: AppState;
  @Output() onSelect = new EventEmitter<string>();
  @Output() onDirectPaymentComplete = new EventEmitter<{ receiptKey: string }>();
  @Output() onDirectPaymentFailed = new EventEmitter<any>();
  @Input() livesplit: boolean = false;
  @Input() disabled: boolean = false;

  // Inputs for direct wallet buttons (Apple Pay / Google Pay).
  @Input() totalSek: number = 0;
  @Input() tipParam: string = '';
  @Input() payableRefs?: string;
  @Input() chargeItems?: ChargeItem[];
  @Input() plKey?: string;
  @Input() itemRefs?: any[];
  @Input() receiptKey?: string;

  constructor(private authService: AuthService, private cdr: ChangeDetectorRef, protected translate: TranslateService) {}

  viva: boolean = false;
  googlePayMode: string = '';
  cardPayment: boolean = false;
  swishPayment: boolean = false;
  vippsPayment: boolean = false;
  prepaid: boolean = false;
  prepaidName?: string;
  prepaidLeft?: string;
  prepaidPrice?: string;
  invoicePayment: boolean = false;
  accountEntryPayment: boolean = false;
  bamboraMerchantNumber?: string;

  // Wallet orchestration state.
  applePayState: WalletButtonState = 'pending';
  googlePayState: WalletButtonState = 'pending';
  walletFirstResolveDone = false;
  walletShowSpinner = false;
  showOtherMethods = false;
  paymentProcessing = false;
  private walletSpinnerTimer?: any;

  // Where Bambora redirects the browser if the bank declines the wallet payment.
  get declineRoute(): string {
    return this.livesplit ? 'livesplit/paymentfailed' : 'cart/paymentfailed';
  }

  // Direct mode: render the native wallet buttons (Apple Pay / Google Pay).
  get isDirectMode(): boolean {
    return this.cardPayment && this.googlePayMode === 'direct' && this.totalSek > 0;
  }

  // ApplePaySession exists in all WebKit browsers on Apple devices (iPhone/iPad/Mac Safari, iOS
  // Chrome). There Google Pay runs as a pay.google.com login popup — confusing as the hero button,
  // so on Apple platforms Apple Pay is the only wallet we promote.
  private readonly applePlatform = typeof (window as any).ApplePaySession !== 'undefined';

  // Google Pay is the promoted (hero) wallet only off Apple platforms, and only when Apple Pay
  // didn't resolve to ready.
  get googlePayIsHero(): boolean {
    return !this.applePlatform && this.applePayState !== 'ready';
  }

  // At least one wallet resolved to a usable hero button.
  get walletAvailable(): boolean {
    return this.applePayState === 'ready' || (this.googlePayState === 'ready' && this.googlePayIsHero);
  }

  // Google Pay renders as hero, or demoted among the other methods (under "Betala på annat sätt"
  // when Apple Pay is the hero, or directly in the list when no hero wallet is available).
  get showGooglePayButton(): boolean {
    return this.googlePayState === 'ready' && (this.googlePayIsHero || this.showOtherButtons);
  }

  // Small "pay another way" link: only while the wallets (and prepaid, if any) are the only options on offer.
  get showOtherMethodsLink(): boolean {
    return this.isDirectMode && this.walletFirstResolveDone && this.walletAvailable && !this.showOtherMethods;
  }

  // The remaining (non-wallet, non-prepaid) payment buttons. Hidden in direct mode until the wallets resolve;
  // once resolved, shown only if no wallet is available or the user opted to pay another way.
  get showOtherButtons(): boolean {
    if (!this.isDirectMode) return true;
    return this.walletFirstResolveDone && (!this.walletAvailable || this.showOtherMethods);
  }

  ngOnInit(): void {
    this.updateValues();
    this.startWalletSpinnerTimer();
  }

  ngOnChanges() {
    this.updateValues();
  }

  ngOnDestroy(): void {
    clearTimeout(this.walletSpinnerTimer);
  }

  private startWalletSpinnerTimer() {
    // Show spinner only if both buttons haven't resolved within 250ms — avoids flash on fast devices.
    this.walletSpinnerTimer = setTimeout(() => {
      if (!this.walletFirstResolveDone) {
        this.walletShowSpinner = true;
        this.cdr.detectChanges();
      }
    }, 250);
  }

  onApplePayStateChange(s: WalletButtonState) {
    this.applePayState = s;
    this.checkWalletResolution();
  }

  onGooglePayStateChange(s: WalletButtonState) {
    this.googlePayState = s;
    this.checkWalletResolution();
  }

  onWalletProcessingChange(processing: boolean) {
    this.paymentProcessing = processing;
    this.cdr.detectChanges();
  }

  private checkWalletResolution() {
    if (this.walletFirstResolveDone) return;
    // If Google Pay isn't rendered (no merchant number), treat it as resolved/unavailable.
    const googleResolved = !this.bamboraMerchantNumber || this.googlePayState !== 'pending';
    const appleResolved = this.applePayState !== 'pending';
    if (appleResolved && googleResolved) {
      this.walletFirstResolveDone = true;
      this.walletShowSpinner = false;
      clearTimeout(this.walletSpinnerTimer);
      this.cdr.detectChanges();
    }
  }

  private updateValues() {
    if (this.appState.cfg.payment) {
      const isKiosk = this.authService.isKioskMode();

      this.viva = !isKiosk && this.appState.cfg.payment.viva != null;
      this.googlePayMode = this.appState.cfg.payment.google_pay_mode;
      this.bamboraMerchantNumber = this.appState.cfg.payment.bambora_merchant_number;
      this.cardPayment = !isKiosk && this.appState.cfg.payment.card;
      this.swishPayment = this.appState.cfg.payment.swish;
      // Only when settling a bill (the server refuses Vipps for cart pre-orders: capture at authorization), and
      // never on a kiosk: the flow redirects this browser to Vipps, which needs the guest's own phone.
      this.vippsPayment = !isKiosk && this.livesplit && !!this.appState.cfg.payment.vipps;
      const balance = this.appState.order?.user_cfg.payment?.prepaid_balance
      if (!this.livesplit && balance != null) {
        this.prepaid = !isKiosk && !this.appState.order?.user_cfg.payment?.open_order;
        this.prepaidName = this.appState.cfg.payment.prepaid_name;
        this.prepaidLeft = MoneyUtils.formatNoCents(balance);
        this.prepaidPrice = MoneyUtils.formatNoCents(this.appState.order.total_pre);
      } else {
        this.prepaid = false;
      }

      if (!this.livesplit) {
        this.invoicePayment = !isKiosk && this.appState.order?.user_cfg.payment?.invoice_payment == 'fortnox_invoice';
        this.accountEntryPayment = !isKiosk && this.appState.order?.user_cfg.payment?.invoice_payment == 'account_entry';
      }
    }
  }

  pay(method: string) {
    console.log("PaymentSelectorComponent pay: " + method);
    this.onSelect.emit(method);
  }
}
